import { useRole } from "hooks/common";
import { useMemo } from "react";
import { TrueOrFalseExerciseContent } from "content";
import { useLessonSettings } from "hooks/redux";
import { Space } from "components/Common/Space";
import { InfoRadioButton } from "components/Interactive/InfoRadioButton";
import { Paragraph } from "components/Typography/Paragraph";
import "./CarouselSlide.styles.scss";

type TCarouselSlideProps = {
  /**
   * Id of the exercise sentence item.
   */
  id: string;
  /**
   * Text of the exercise sentence item.
   */
  sentenceText: string;
  /**
   * Flag that indicates whether the sentence is correct or not.
   */
  isSentenceCorrect: boolean;
  /**
   * Callback that is called when user clicks on the sentence answer.
   * @param sentenceId Id of the sentence item.
   * @param isUserAnswerCorrect Flag that indicates whether the user answer is correct or not.
   */
  onAnswerClick?: (sentenceId: string, isUserAnswerCorrect: boolean) => void;
  userSelected: false | "true" | "false";
};

export const CarouselSlide: React.FC<TCarouselSlideProps> = (props) => {
  const { id, sentenceText, isSentenceCorrect, onAnswerClick, userSelected } =
    props;

  const { isUserTeacher } = useRole();
  const { isExerciseReadOnlyMode, isExercisePreviewMode, isExerciseLiveMode } =
    useLessonSettings();

  const isTrueChecked = useMemo(
    () => (isExercisePreviewMode ? isSentenceCorrect : userSelected === "true"),
    [isExercisePreviewMode, isSentenceCorrect, userSelected]
  );

  const isFalseChecked = useMemo(
    () =>
      isExercisePreviewMode ? !isSentenceCorrect : userSelected === "false",
    [isExercisePreviewMode, isSentenceCorrect, userSelected]
  );

  const isDisabled = useMemo(
    () => isExerciseReadOnlyMode || userSelected !== false,
    [isExerciseReadOnlyMode, userSelected]
  );

  const answerClickButtonHandler = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isDisabled) return;

    const isCorrectAnswer = evt.target.value === isSentenceCorrect.toString();

    onAnswerClick?.(id, isCorrectAnswer);
  };

  return (
    <Space
      direction="vertical"
      justify="stretch"
      align="center"
      size="medium"
      fullWidth
    >
      <Paragraph variant={2} bold>
        {sentenceText}
      </Paragraph>
      <Space
        direction="horizontal"
        justify="center"
        align="center"
        size="small"
      >
        <InfoRadioButton
          id={`${id}-true`}
          name={id}
          isCorrect={isSentenceCorrect}
          value="true"
          bordered
          checked={isTrueChecked}
          fullWidth
          onChange={answerClickButtonHandler}
          disabled={isExercisePreviewMode || isUserTeacher || isDisabled}
        >
          {TrueOrFalseExerciseContent.Option.TRUE}
        </InfoRadioButton>
        <InfoRadioButton
          id={`${id}-false`}
          name={id}
          isCorrect={!isSentenceCorrect}
          value="false"
          bordered
          checked={isFalseChecked}
          onChange={answerClickButtonHandler}
          disabled={isExercisePreviewMode || isDisabled || isUserTeacher}
        >
          {TrueOrFalseExerciseContent.Option.FALSE}
        </InfoRadioButton>
      </Space>
    </Space>
  );
};
